import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useApp } from "../store";
import { completeTask, updateTask } from "../sync/actions";
import { openTask } from "../tasks/TaskRow";
import { hasOpenLayer } from "../ui/primitives";

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
}

function visibleTaskIds(): string[] {
  return [...document.querySelectorAll<HTMLElement>(".content [data-task-id]")].map((e) => e.dataset.taskId ?? "");
}

/** Todoist-style single-key shortcuts (q, /, g+i/t/u/f, j/k, e, Enter, 1-4, m, ?). */
export function useKeyboardShortcuts() {
  const navigate = useNavigate();
  useEffect(() => {
    let pendingG = 0;
    const handler = (e: KeyboardEvent) => {
      const app = useApp.getState();
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        app.set({ searchOpen: true });
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target) || e.isComposing) return;
      if (hasOpenLayer() || new URLSearchParams(window.location.search).has("task")) return;
      const key = e.key;
      if (pendingG && Date.now() - pendingG < 1500) {
        pendingG = 0;
        const dest = {
          i: "/app/inbox",
          t: "/app/today",
          u: "/app/upcoming",
          f: "/app/filters-labels",
          l: "/app/filters-labels",
        }[key.toLowerCase()];
        if (dest) {
          e.preventDefault();
          navigate(dest);
        }
        return;
      }
      const focused = app.focusedTaskId;
      const engine = app.engine;
      switch (key) {
        case "q":
          e.preventDefault();
          app.set({ quickAdd: { open: true } });
          break;
        case "/":
        case "f":
          e.preventDefault();
          app.set({ searchOpen: true });
          break;
        case "g":
          pendingG = Date.now();
          break;
        case "m":
          app.set({ sidebarOpen: !app.sidebarOpen });
          break;
        case "?":
          app.set({ shortcutsOpen: true });
          break;
        case "j":
        case "k":
        case "ArrowDown":
        case "ArrowUp": {
          const ids = visibleTaskIds();
          if (!ids.length) return;
          e.preventDefault();
          const i = focused ? ids.indexOf(focused) : -1;
          const down = key === "j" || key === "ArrowDown";
          const next =
            i < 0 ? ids[down ? 0 : ids.length - 1] : ids[Math.max(0, Math.min(ids.length - 1, i + (down ? 1 : -1)))];
          app.set({ focusedTaskId: next ?? null });
          document.querySelector(`.content [data-task-id="${next}"]`)?.scrollIntoView({ block: "nearest" });
          break;
        }
        case "Enter":
          if (focused && visibleTaskIds().includes(focused)) {
            e.preventDefault();
            openTask(navigate, focused);
          }
          break;
        case "e": {
          const item = focused && engine?.state.items[focused];
          if (item && !item.checked) {
            e.preventDefault();
            const ids = visibleTaskIds();
            const i = ids.indexOf(item.id);
            completeTask(item);
            app.set({ focusedTaskId: ids[i + 1] ?? ids[i - 1] ?? null });
          }
          break;
        }
        case "1":
        case "2":
        case "3":
        case "4":
          if (focused && engine?.state.items[focused]) {
            e.preventDefault();
            void updateTask(focused, { priority: 5 - Number(key) });
          }
          break;
        case "Escape":
          app.set({ focusedTaskId: null });
          break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate]);
}
